import { useState, type ReactNode } from 'react'
import { ArrowUUpLeft, Notebook, Plus, UserPlus } from '@phosphor-icons/react'
import { Badge, Banner, Button, DataTable, EmptyState, Field, Input, IntegerInput, Modal, TextArea, type Column, type Tone } from '@/ui'
import { errorMessage } from '../api/core'
import {
  missingSlips,
  useIssueReceiptBook,
  useReceiptBook,
  useReceiptBooks,
  useRegisterReceiptBook,
  useReturnReceiptBook,
  type ReceiptBookDetail,
  type ReceiptBookRow,
} from '../api/gate'
import type { UserOption } from '../api/reference'
import { ActionButton } from '../components/Can'
import { DetailDrawer } from '../components/DetailDrawer'
import { FilterBar, SelectFilter } from '../components/FilterBar'
import { FormDialog } from '../components/FormDialog'
import { ConsolePage, QueryState } from '../components/Layout'
import { PersonPicker } from '../components/PersonPicker'
import { Code, Facts, RefLink, Section, Status, UserName, When } from '../components/bits'
import { useUrlState } from '../lib/useUrlState'
import { formatSlipRanges, parseSlipList, slipCount, validateBookForm, type BookForm } from '../model/receiptBookForm'

const LIMIT = 25

const BOOK_STATUS: Record<string, { label: string; tone: Tone }> = {
  PRINTED: { label: 'In the office', tone: 'neutral' },
  ISSUED: { label: 'Issued', tone: 'info' },
  RETURNED: { label: 'Returned', tone: 'ok' },
  VOID: { label: 'Void', tone: 'danger' },
}
function BookStatus({ status }: { status: string }) {
  const s = BOOK_STATUS[status] ?? { label: status, tone: 'neutral' as Tone }
  return <Badge tone={s.tone}>{s.label}</Badge>
}

type Dialog = { kind: 'issue' | 'return'; book: ReceiptBookRow } | null

/** Receipt books (F4-B3.a): printed slip books for paper days — register, issue to an operator, take back. */
export function ReceiptBooksPage() {
  const [f, setF, { reset, active }] = useUrlState({ status: '', page: 1, open: '' })
  const [registering, setRegistering] = useState(false)
  const [dialog, setDialog] = useState<Dialog>(null)
  const q = useReceiptBooks({ page: f.page, limit: LIMIT, status: f.status || undefined })

  const actions = (b: ReceiptBookRow) => (
    <div className="flex flex-wrap gap-2" onClick={(e) => e.stopPropagation()}>
      {(b.status === 'PRINTED' || b.status === 'ISSUED') && (
        <ActionButton perm="receipt-books:issue" size="sm" variant="secondary" leadingIcon={<UserPlus />} onClick={() => setDialog({ kind: 'issue', book: b })}>
          {b.status === 'ISSUED' ? 'Reissue' : 'Issue'}
        </ActionButton>
      )}
      {b.status === 'ISSUED' && (
        <ActionButton perm="receipt-books:return" size="sm" variant="secondary" leadingIcon={<ArrowUUpLeft />} onClick={() => setDialog({ kind: 'return', book: b })}>
          Return
        </ActionButton>
      )}
    </div>
  )

  const columns: Column<ReceiptBookRow>[] = [
    { id: 'book', header: 'Book', cell: (r) => <span className="font-mono text-[14px] font-bold">{r.bookNo}</span> },
    {
      id: 'slips',
      header: 'Slips',
      cell: (r) => (
        <span className="flex flex-col">
          <Code>
            {r.firstSlipNo}–{r.lastSlipNo}
          </Code>
          <span className="text-[12px] text-muted">{slipCount(r.firstSlipNo, r.lastSlipNo)} slips</span>
        </span>
      ),
    },
    { id: 'holder', header: 'Issued to', cell: (r) => (r.issuedToId ? <UserName id={r.issuedToId} /> : <span className="text-muted">—</span>), hideBelow: 'sm' },
    {
      id: 'use',
      header: 'Used · spoiled · left',
      align: 'right',
      mono: true,
      cell: (r) => {
        const left = slipCount(r.firstSlipNo, r.lastSlipNo) - r.used - r.spoiled
        return (
          <span>
            {r.used} · <span className={r.spoiled ? 'text-warn' : undefined}>{r.spoiled}</span> · <span className="text-muted">{left}</span>
          </span>
        )
      },
      hideBelow: 'md',
    },
    { id: 'issued', header: 'Issued', cell: (r) => <When at={r.issuedAt} dateOnly />, hideBelow: 'lg' },
    { id: 'returned', header: 'Returned', cell: (r) => <When at={r.returnedAt} dateOnly />, hideBelow: 'lg' },
    { id: 'status', header: 'Status', cell: (r) => <BookStatus status={r.status} /> },
    { id: 'actions', header: <span className="sr-only">Actions</span>, cell: actions, hideBelow: 'md' },
  ]

  return (
    <ConsolePage
      eyebrow="Farm gate"
      title="Receipt books"
      description="Numbered slip books for days when the phone cannot be used. Register each printed book, issue it to an operator, and take it back once every slip is written, spoiled or still blank."
      actions={
        <ActionButton perm="receipt-books:create" leadingIcon={<Plus />} onClick={() => setRegistering(true)}>
          Register a book
        </ActionButton>
      }
    >
      <DataTable<ReceiptBookRow>
        caption="Receipt books"
        columns={columns}
        rows={q.data?.rows ?? []}
        getRowId={(r) => r.id}
        loading={q.isLoading}
        error={q.isError ? errorMessage(q.error) : undefined}
        pageSize={LIMIT}
        server={{ page: f.page, total: q.data?.total ?? 0, onPageChange: (page) => setF({ page }) }}
        onRowClick={(r) => setF({ open: r.id, page: f.page })}
        filters={
          <FilterBar onClear={reset} active={active}>
            <SelectFilter
              label="Status"
              value={f.status as keyof typeof BOOK_STATUS | ''}
              onChange={(status) => setF({ status })}
              options={(['PRINTED', 'ISSUED', 'RETURNED', 'VOID'] as const).map((s) => ({ value: s, label: BOOK_STATUS[s]!.label }))}
            />
          </FilterBar>
        }
        empty={
          <EmptyState
            icon={<Notebook />}
            title={active ? 'No books match' : 'No receipt books yet'}
            description={active ? 'Try clearing the filter.' : 'Register a printed book with its first and last slip number.'}
          />
        }
      />
      <RegisterDialog open={registering} onClose={() => setRegistering(false)} />
      <IssueDialog book={dialog?.kind === 'issue' ? dialog.book : null} onClose={() => setDialog(null)} />
      <ReturnDialog book={dialog?.kind === 'return' ? dialog.book : null} onClose={() => setDialog(null)} />
      <BookDrawer id={f.open || null} onClose={() => setF({ open: '', page: f.page })} actions={actions} />
    </ConsolePage>
  )
}

function RegisterDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [form, setForm] = useState<BookForm>({ bookNo: '', firstSlipNo: null, lastSlipNo: null })
  const [touched, setTouched] = useState(false)
  const [wasOpen, setWasOpen] = useState(open)
  if (open !== wasOpen) {
    setWasOpen(open)
    if (open) {
      setForm({ bookNo: '', firstSlipNo: null, lastSlipNo: null })
      setTouched(false)
    }
  }
  const register = useRegisterReceiptBook()
  const errors = validateBookForm(form)
  const valid = Object.keys(errors).length === 0
  const count = form.firstSlipNo !== null && form.lastSlipNo !== null && form.lastSlipNo >= form.firstSlipNo ? slipCount(form.firstSlipNo, form.lastSlipNo) : null
  return (
    <FormDialog
      open={open}
      onClose={onClose}
      title="Register a receipt book"
      description="Copy the numbers printed on the cover and on the first and last slip."
      saveLabel="Register"
      loading={register.isPending}
      onSave={() => {
        setTouched(true)
        if (!valid) throw new Error('invalid')
        return register.mutateAsync({ bookNo: form.bookNo.trim(), firstSlipNo: form.firstSlipNo!, lastSlipNo: form.lastSlipNo! })
      }}
    >
      <Field label="Book number" required error={touched ? errors.bookNo : null}>
        <Input value={form.bookNo} onChange={(e) => setForm((f) => ({ ...f, bookNo: e.target.value }))} maxLength={20} className="font-mono" placeholder="e.g. 0008" data-autofocus />
      </Field>
      <div className="grid grid-cols-2 gap-4">
        <Field label="First slip" required error={touched ? errors.firstSlipNo : null}>
          <IntegerInput value={form.firstSlipNo} onChange={(firstSlipNo) => setForm((f) => ({ ...f, firstSlipNo }))} maxDigits={9} />
        </Field>
        <Field label="Last slip" required error={touched ? errors.lastSlipNo : null} hint={count !== null && !errors.lastSlipNo ? `${count} slips` : undefined}>
          <IntegerInput value={form.lastSlipNo} onChange={(lastSlipNo) => setForm((f) => ({ ...f, lastSlipNo }))} maxDigits={9} />
        </Field>
      </div>
    </FormDialog>
  )
}

function IssueDialog({ book, onClose }: { book: ReceiptBookRow | null; onClose: () => void }) {
  const [person, setPerson] = useState<UserOption | null>(null)
  const [was, setWas] = useState(book)
  if (book !== was) {
    setWas(book)
    if (book) setPerson(null)
  }
  const issue = useIssueReceiptBook()
  return (
    <FormDialog
      open={book !== null}
      onClose={onClose}
      title={book ? `Issue book ${book.bookNo}` : 'Issue book'}
      description={book ? `Slips ${book.firstSlipNo}–${book.lastSlipNo}. The operator's phone gets the book with its next farmer-list update.` : undefined}
      saveLabel="Issue"
      canSave={Boolean(person)}
      loading={issue.isPending}
      onSave={() => (book && person ? issue.mutateAsync({ id: book.id, issuedToId: person.id }) : undefined)}
    >
      {book?.issuedToId && (
        <p className="text-[14px] text-muted">
          Now with <UserName id={book.issuedToId} />.
        </p>
      )}
      <Field label="Operator" required>
        <PersonPicker value={person} onChange={setPerson} placeholder="Search the operator" label="Operator" />
      </Field>
    </FormDialog>
  )
}

function ReturnDialog({ book, onClose }: { book: ReceiptBookRow | null; onClose: () => void }) {
  const [text, setText] = useState('')
  const [missing, setMissing] = useState<number[]>([])
  const [touched, setTouched] = useState(false)
  const [was, setWas] = useState(book)
  if (book !== was) {
    setWas(book)
    if (book) {
      setText('')
      setMissing([])
      setTouched(false)
    }
  }
  const detail = useReceiptBook(book?.id ?? null)
  const ret = useReturnReceiptBook((e) => {
    const m = missingSlips(e)
    setMissing(m)
    return m.length > 0
  })
  const parsed = book ? parseSlipList(text, book.firstSlipNo, book.lastSlipNo) : { slips: [], error: null }
  const unrecorded = detail.data ? unrecordedSlips(detail.data) : []

  const submit = async () => {
    setTouched(true)
    if (!book || parsed.error) return
    setMissing([])
    try {
      await ret.mutateAsync({ id: book.id, blankSlipsInHand: parsed.slips })
      onClose()
    } catch {
      /* missing slips shown below; anything else is toasted */
    }
  }

  return (
    <Modal
      open={book !== null}
      onClose={onClose}
      title={book ? `Return book ${book.bookNo}` : 'Return book'}
      description="A book can be returned only when every slip is written on, spoiled, or handed back blank."
      dismissible={!ret.isPending}
      footer={
        <>
          <Button variant="ghost" className="ml-auto" onClick={onClose} disabled={ret.isPending}>
            Cancel
          </Button>
          <Button loading={ret.isPending} onClick={() => void submit()}>
            Record return
          </Button>
        </>
      }
    >
      {book && (
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault()
            void submit()
          }}
        >
          <Facts
            columns={3}
            items={[
              { label: 'Slips', value: `${book.firstSlipNo}–${book.lastSlipNo}`, mono: true },
              { label: 'Used · spoiled', value: `${book.used} · ${book.spoiled}`, mono: true },
              { label: 'Not entered', value: detail.data ? unrecorded.length : '…', mono: true },
            ]}
          />
          <Field
            label="Blank slips handed back"
            error={touched ? parsed.error : null}
            hint={
              parsed.slips.length
                ? `${parsed.slips.length} slip${parsed.slips.length === 1 ? '' : 's'}: ${formatSlipRanges(parsed.slips)}`
                : 'Slip numbers or ranges, e.g. 446-449, 452. Leave empty if none are left.'
            }
            aside={
              unrecorded.length > 0 && (
                <button type="button" className="text-[12.5px] text-ink underline decoration-rule underline-offset-2 hover:decoration-amber" onClick={() => setText(formatSlipRanges(unrecorded).replace(/–/g, '-'))}>
                  Fill the {unrecorded.length} not entered
                </button>
              )
            }
          >
            <TextArea value={text} onChange={(e) => setText(e.target.value)} rows={2} className="font-mono" data-autofocus />
          </Field>
          {missing.length > 0 && (
            <Banner tone="error" title={`${missing.length} slip${missing.length === 1 ? ' is' : 's are'} not accounted for`}>
              <span className="font-mono">{formatSlipRanges(missing)}</span>
              <span className="mt-1 block">
                Enter them from the paper-day review if they were written on, or add them above if the operator handed them back blank.
              </span>
            </Banner>
          )}
        </form>
      )}
    </Modal>
  )
}

/** Slip numbers of the book with no USED / SPOILED record yet. */
function unrecordedSlips(b: ReceiptBookDetail): number[] {
  const seen = new Set(b.slips.map((s) => s.slipNo))
  const out: number[] = []
  for (let n = b.firstSlipNo; n <= b.lastSlipNo; n++) if (!seen.has(n)) out.push(n)
  return out
}

function BookDrawer({ id, onClose, actions }: { id: string | null; onClose: () => void; actions: (b: ReceiptBookRow) => ReactNode }) {
  const q = useReceiptBook(id)
  const b = q.data
  const blank = b ? unrecordedSlips(b) : []
  return (
    <DetailDrawer
      open={Boolean(id)}
      onClose={onClose}
      width="lg"
      eyebrow="Receipt book"
      title={b ? <span className="font-mono">{b.bookNo}</span> : 'Receipt book'}
      meta={b && <BookStatus status={b.status} />}
      actions={b && actions(b)}
    >
      <QueryState query={q} label="the book">
        {b && (
          <div className="flex flex-col gap-5">
            <Facts
              columns={3}
              items={[
                { label: 'Slips', value: `${b.firstSlipNo}–${b.lastSlipNo} (${slipCount(b.firstSlipNo, b.lastSlipNo)})`, mono: true },
                { label: 'Issued to', value: <UserName id={b.issuedToId} /> },
                { label: 'Issued', value: <When at={b.issuedAt} /> },
                { label: 'Used', value: b.used, mono: true },
                { label: 'Spoiled', value: b.spoiled, mono: true },
                { label: 'Returned', value: <When at={b.returnedAt} /> },
              ]}
            />
            {blank.length > 0 && (
              <p className="text-[14px] text-muted">
                Not entered yet: <span className="font-mono text-ink">{formatSlipRanges(blank)}</span>
              </p>
            )}
            <Section title="Slips entered" eyebrow={`${b.slips.length} of ${slipCount(b.firstSlipNo, b.lastSlipNo)}`} bodyClassName="p-0">
              {b.slips.length === 0 ? (
                <p className="px-4 py-3 text-[14px] text-muted">No slips entered from this book yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-[14px]">
                    <thead>
                      <tr className="border-b border-rule text-left font-mono text-[10.5px] uppercase tracking-label text-muted">
                        <th className="px-4 py-2">Slip</th>
                        <th className="px-2 py-2">Status</th>
                        <th className="px-2 py-2">Lot</th>
                        <th className="px-2 py-2">Check</th>
                        <th className="hidden px-4 py-2 sm:table-cell">Entered</th>
                      </tr>
                    </thead>
                    <tbody>
                      {b.slips.map((s) => (
                        <tr key={s.id} className="border-b border-rule last:border-0">
                          <td className="px-4 py-2 font-mono">{s.slipNo}</td>
                          <td className="px-2 py-2">
                            <Badge tone={s.status === 'SPOILED' ? 'warn' : 'neutral'}>{s.status === 'SPOILED' ? 'Spoiled' : 'Written'}</Badge>
                          </td>
                          <td className="px-2 py-2">
                            {s.unitId ? <RefLink to={`/c/lots/${s.unitId}`}>Open lot</RefLink> : s.sessionId ? <RefLink to={`/c/sessions/${s.sessionId}`}>Day</RefLink> : '—'}
                          </td>
                          <td className="px-2 py-2">{s.reviewResult ? <Status status={s.reviewResult} /> : <span className="text-muted">Not checked</span>}</td>
                          <td className="hidden px-4 py-2 sm:table-cell">
                            <When at={s.enteredAt} /> · <UserName id={s.enteredById} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Section>
          </div>
        )}
      </QueryState>
    </DetailDrawer>
  )
}
